/**
 * Validates if a given URL is a valid SAM.gov solicitation URL
 * Valid formats:
 * - https://sam.gov/opp/{opportunity-id}/view
 * - https://sam.gov/opportunity/{opportunity-id}
 * 
 * Also checks if the opportunity is a solicitation notice
 */
export const validateSAMUrl = (url: string): { isValid: boolean; message?: string } => {
  try {
    const parsedUrl = new URL(url);
    
    // Check if it's a sam.gov URL
    if (!parsedUrl.hostname.endsWith('sam.gov')) {
      return {
        isValid: false,
        message: 'Please enter a valid SAM.gov URL'
      };
    }

    // Check if it's an opportunity URL
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    if (
      !(
        (pathSegments[0] === 'opp' && pathSegments.length === 3 && pathSegments[2] === 'view') ||
        (pathSegments[0] === 'opportunity' && pathSegments.length === 2)
      )
    ) {
      return {
        isValid: false,
        message: 'Please enter a valid SAM.gov opportunity URL'
      };
    }

    // Extract and validate opportunity ID
    const opportunityId = pathSegments[1];
    if (!opportunityId || opportunityId.length < 8) {
      return {
        isValid: false,
        message: 'Invalid opportunity ID in URL'
      };
    }

    // Check for notice type in query parameters
    const params = new URLSearchParams(parsedUrl.search);
    const noticeType = params.get('noticeType');
    
    if (noticeType && !isValidSolicitationType(noticeType)) {
      return {
        isValid: false,
        message: 'This opportunity is not a solicitation notice'
      };
    }

    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      message: 'Please enter a valid URL'
    };
  }
};

/**
 * Valid solicitation notice types from SAM.gov
 */
const VALID_SOLICITATION_TYPES = [
  'PRESOL', // Pre-solicitation
  'COMBINE', // Combined Synopsis/Solicitation
  'MOD', // Solicitation Amendment
  'AWARD', // Award Notice
  'SRCSGT', // Sources Sought
  'SSALE', // Sale of Surplus Property
  'SNOTE', // Special Notice
  'ITB', // Invitation for Bid
  'RFP', // Request for Proposal
  'RFI', // Request for Information
  'RFQ', // Request for Quote
];

/**
 * Checks if the notice type is a valid solicitation type
 */
export const isValidSolicitationType = (noticeType: string): boolean => {
  return VALID_SOLICITATION_TYPES.includes(noticeType.toUpperCase());
};

/**
 * Extracts the opportunity ID from a valid SAM.gov URL
 */
export const extractOpportunityId = (url: string): string | null => {
  try {
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    return pathSegments[1] || null;
  } catch {
    return null;
  }
};

/**
 * Normalizes a SAM.gov URL to a consistent format
 */
export const normalizeSAMUrl = (url: string): string => {
  const opportunityId = extractOpportunityId(url);
  if (!opportunityId) return url;
  return `https://sam.gov/opp/${opportunityId}/view`;
};

/**
 * Gets the notice type from a SAM.gov URL
 */
export const getNoticeType = (url: string): string | null => {
  try {
    const parsedUrl = new URL(url);
    const params = new URLSearchParams(parsedUrl.search);
    return params.get('noticeType');
  } catch {
    return null;
  }
};
